import {
  Box,
  Button,
  Colors,
  Text,
  VerticalStackedBarChart,
  VerticalStackedBarChartData,
  VerticalStackedBarChartTooltipItem,
} from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import contractVersions from 'modules/contracts/store/collections/contractVersions'
import contractVersionsPayrollPolicies from 'modules/contracts/store/collections/contractVersionsPayrollPolicies'
import employees from 'modules/employees/store/collections/employees'
import Disclosure from 'modules/payroll_bundle/components/Disclosure'
import { useFetchPolicyPaymentsData } from 'modules/payroll_bundle/hooks/useFetchPolicyPaymentsData'
import {
  ResultConcepts,
  isResultConceptsType,
  resultConceptsLabels,
} from 'modules/payroll_bundle/lib/constants'
import filterActiveEmployeesByPolicy from 'modules/payroll_bundle/lib/filterActiveEmployeesByPolicy'
import { formatAmount } from 'modules/payroll_bundle/lib/formatNumber'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import payments from 'modules/payroll_bundle/store/collections/payments'
import Payment, { PAID } from 'modules/payroll_bundle/store/models/Payment'
import Policy from 'modules/payroll_bundle/store/models/Policy'
import PolicyPeriod from 'modules/payroll_bundle/store/models/PolicyPeriod'

import { usePayrollBundlePageContext } from 'modules/payroll_bundle/components/PayrollBundle'
import EmptyState from './EmptyState'
import SkeletonLoader from './SkeletonLoader'
import * as styles from './index.vanilla.css'

type SegmentColors = Record<ResultConcepts, Colors>

const segmentColors: SegmentColors = {
  [ResultConcepts.bonus_benefit]: 'viridian1000',
  [ResultConcepts.regular_payroll]: 'champagne1000',
  [ResultConcepts.delayed_payment]: 'radicalRed600',
  [ResultConcepts.extra_pay]: 'lime1000',
  [ResultConcepts.rate_difference]: 'malibu1000',
  [ResultConcepts.salary_advance]: 'fuchsia1000',
  [ResultConcepts.severance]: 'purple1000',
} as const

interface Props {
  currency: string
}

const PayrollSummaryWidget = ({ currency }: Props) => {
  const { navigation } = useNavigation()
  const conceptLabels = resultConceptsLabels()
  const { currentPolicyPeriods, currentPeriod } = usePolicyPeriods()

  // Get the 6 last periods inside the current policy and load the payments related to them.
  const policyPeriods = currentPolicyPeriods
  const orderedPolicyPeriods = PolicyPeriod.sortPolicyPeriodsFromOlderToNewer(policyPeriods)

  const PERIOD_LENGTH = 3

  // take the period selected as the last one in the ordered list
  // eslint-disable-next-line no-type-assertion/no-type-assertion
  const currentIndex = orderedPolicyPeriods.indexOf(currentPeriod!)
  const startIndex = Math.max(0, currentIndex - (PERIOD_LENGTH - 1))
  const endIndex = currentIndex

  const currentPeriodAndPreviousOrdered = orderedPolicyPeriods.slice(startIndex, endIndex + 1)

  const currentPeriodAndPreviousIds = currentPeriodAndPreviousOrdered.map((period) => period.id)
  const { isLoading } = useFetchPolicyPaymentsData(currentPeriodAndPreviousIds)

  // Policy periodicity
  const { selectedPolicy } = usePayrollBundlePageContext()
  const periodicity = Policy.periodicityByRecurrence(selectedPolicy.attributes.recurrence)
  const policyIsMonthly = periodicity?.label === 'Monthly'

  const periodStartDate = moment(currentPeriod?.attributes.starts_on)
  const periodEndDate = moment(currentPeriod?.attributes.ends_on)

  const activeEmployees = filterActiveEmployeesByPolicy({
    employees,
    policy: selectedPolicy,
    contractVersions,
    contractVersionsPayrollPolicies,
    period: {
      startsOn: periodStartDate,
      endsOn: periodEndDate,
    },
  })?.length

  const fillEmptyChartData = (data: VerticalStackedBarChartData[]) => {
    if (!data.length || data.length >= PERIOD_LENGTH) return null

    const spotsToFill = PERIOD_LENGTH - data.length

    for (let i = 0; i < spotsToFill; i++) {
      data.unshift({
        id: i + Date.now(),
        label: '-',
        values: [],
      })
    }
  }

  const generateChartData = (): VerticalStackedBarChartData[] => {
    return currentPeriodAndPreviousOrdered.map((period) => {
      const barTitle = policyIsMonthly
        ? moment(period.attributes.starts_on).format('MMM')
        : moment(period.attributes.starts_on).format('MMM DD')

      const labelTitle = moment(period.attributes.starts_on).format('MMMM D Y')
      const tooltipPayments: VerticalStackedBarChartTooltipItem[] = []

      const periodPaymentsGroupedByConcept: [string, Payment[]][] = Object.entries(
        payments.periodPaymentsGroupedByConcept(payments.currentPeriodPayments(period?.id))
      )

      const generatePeriodPaymentsStructuredData = () => {
        return periodPaymentsGroupedByConcept.map(([key, payments]) => {
          if (!isResultConceptsType(key)) throw new Error('Invalid concept type')

          const firstPayment = payments?.[0]
          const conceptLabel = conceptLabels[key]

          // sum all payments that are PAID
          const paymentsSum = payments.reduce(
            (accumulator, paymentItem) =>
              paymentItem.attributes.status === PAID
                ? accumulator + paymentItem.paymentsTotalAmount
                : accumulator,
            0
          )

          const segmentColor = segmentColors?.[key] ?? 'grey600'

          tooltipPayments.push({
            id: firstPayment?.id,
            itemTitle: conceptLabel,
            itemDescription: formatAmount(paymentsSum, currency),
            color: segmentColor,
            value: paymentsSum,
            typeValue: key,
          })

          return {
            id: firstPayment?.id,
            value: paymentsSum,
            color: segmentColor,
            typeValue: key,
          }
        })
      }

      const periodPayments =
        periodPaymentsGroupedByConcept?.length > 0 ? generatePeriodPaymentsStructuredData() : []

      return {
        id: period.id,
        values: periodPayments,
        label: barTitle,
        showTooltip: true,
        tooltipContent: {
          title: labelTitle,
          items: tooltipPayments,
        },
      }
    })
  }

  const generateDetailsData = () => {
    const policyTotals = payments.getPolicyPeriodTotals(currentPeriod?.id)

    return {
      numberOfEmployeesPaid: policyTotals.numberOfEmployeesPaid,
      numberOfEmployeesInPolicy: activeEmployees,
      totalPaidAmount: policyTotals.totalPaidAmount,
    }
  }

  const chartData = generateChartData()
  fillEmptyChartData(chartData)
  const lastElementOfChartData = chartData.slice(-1)

  const paymentDetailsData = generateDetailsData()

  // check that we have results in the chart dataset object
  const chartHasValues = chartData.filter((chartItem) => chartItem.values.length > 0)
  const lastElementOfChartHasValues = chartHasValues.slice(-1)

  if (isLoading) {
    return <SkeletonLoader />
  }

  if (
    !isLoading &&
    (chartHasValues.length === 0 ||
      lastElementOfChartData[0]?.id !== lastElementOfChartHasValues[0]?.id)
  ) {
    return <EmptyState />
  }

  return (
    <Box gap="s8" style={{ flex: '1' }}>
      <Box height="s32" flexDirection="row" alignItems="center" paddingX="s8">
        <Text.Normal weight="medium" size="300">
          {i18n.t('payroll_bundle.payroll_dashboard.payment_summary.widget_title')}
        </Text.Normal>
        <Disclosure
          tooltipText={i18n.t(
            'payroll_bundle.payroll_dashboard.payment_summary.heading_tooltip.tooltip_v2'
          )}
        />
      </Box>
      <Box
        flexDirection="row"
        border={{ all: { color: 'grey400', width: 's1', style: 'solid' } }}
        borderRadius={{ all: 'abs016' }}
        minHeight="s200"
        style={{ flex: '1' }}
      >
        <Box
          style={{ flex: '1' }}
          padding={'s24'}
          gap="s16"
          minWidth="s200"
          justifyContent="spaceBetween"
        >
          <Box gap="s16">
            <Box paddingBottom={'s8'}>
              <Text size="300" weight="medium" color="grey1000">
                {paymentDetailsData.numberOfEmployeesPaid} /{' '}
                {paymentDetailsData.numberOfEmployeesInPolicy}
              </Text>
              <Text size="300">
                {i18n.t('payroll_bundle.payroll_dashboard.payment_summary.employees_paid')}
              </Text>
            </Box>
            <Box>
              <Text size="300" weight="medium" color="grey1000">
                {formatAmount(paymentDetailsData.totalPaidAmount, currency)}
              </Text>
              <Text size="300">
                {i18n.t('payroll_bundle.payroll_dashboard.payment_summary.total_paid')}
              </Text>
            </Box>
          </Box>
          <Box maxWidth="fit-content">
            <Button.Router
              type="secondary"
              disabled={!currentPeriod}
              to={navigation.payrollBundle.policy.period.cycles.results.fullPath({
                policyId: selectedPolicy.id,
                periodId: currentPeriod?.id,
              })}
            >
              {i18n.t('payroll_bundle.payroll_dashboard.payment_summary.upload_results')}
            </Button.Router>
          </Box>
        </Box>
        <Box minWidth="s1" width="s1" className={styles.lineBackground} />
        <Box padding={'s32'} alignItems="center" style={{ flex: '2' }}>
          <VerticalStackedBarChart data={chartData} />
        </Box>
      </Box>
    </Box>
  )
}

export default PayrollSummaryWidget
